import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { toast } from 'react-toastify';
import { userInterface } from '../../app/consts';
import {
  addUser,
  checkAuth,
  getAllUsers,
  login,
  logout,
  updateUserActiveStatus,
  updateUserMfaStatus,
  updateUserPhoneNum
} from './authenticationAPI';

export interface UserData {
  name: string;
  email: string;
  isSU: boolean;
  mfa: boolean;
}

export interface LoginState {
  isLoggedIn: boolean;
  isAuthInitialized: boolean;
  currentUser: UserData | null;
  addUserModalShow: boolean;
  updatePhoneNumModalShow: boolean;
  loginModalShow: boolean;
  isLoading: boolean;
  errorMsg?: string;
  timesCalledLogout: number;
  usersList: Array<userInterface>;
  updateFlag: boolean;
  isOTPCompleted: boolean;
  isReadyForOTP: boolean;
}

const initialState: LoginState = {
  isLoggedIn: false,
  isAuthInitialized: false,
  currentUser: null,
  addUserModalShow: false,
  updatePhoneNumModalShow: false,
  loginModalShow: false,
  isLoading: false,
  timesCalledLogout: 0,
  usersList: [],
  updateFlag: false,
  isOTPCompleted: false,
  isReadyForOTP: false,
};

export const checkAuthAsync = createAsyncThunk(
  'authentication/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      return await checkAuth();
    } catch (err: any) {
      return rejectWithValue(null);
    }
  }
);

export const LoginAsync = createAsyncThunk(
  'authentication/Login',
  async (credentials: { username: string; password: string }, { rejectWithValue }) => {
    try {
      return await login(credentials);
    } catch (err: any) {
      const msg = err.response?.data?.detail || "שם משתמש או סיסמא שגויים";
      return rejectWithValue(msg);
    }
  }
);

export const LogoutAsync = createAsyncThunk(
  'authentication/Logout',
  async (_, { dispatch }) => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout request failed on server, clearing client state anyway");
    } finally {
      dispatch(localLogout("manual"));
    }
  }
);

export const addUserAsync = createAsyncThunk(
  'authentication/addUser',
  async (data: userInterface, { dispatch, rejectWithValue }) => {
    try {
      return await addUser(data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getAllUsersAsync = createAsyncThunk(
  'authentication/getAllUsers',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      return await getAllUsers();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateUserActiveStatusAsync = createAsyncThunk(
  'authentication/updateUserActiveStatus',
  async (username: string, { dispatch, rejectWithValue }) => {
    try {
      return await updateUserActiveStatus(username);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateUserPhoneNumAsync = createAsyncThunk(
  'authentication/updateUserPhoneNum',
  async (data: { username: string; phone_num: string }, { dispatch, rejectWithValue }) => {
    try {
      return await updateUserPhoneNum(data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateUserMfaStatusAsync = createAsyncThunk(
  'authentication/updateUserMfaStatus',
  async (username: string, { dispatch, rejectWithValue }) => {
    try {
      return await updateUserMfaStatus(username);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    // Synchronously resets auth state and opens the modal if triggered by expiry
    localLogout: (state, action: PayloadAction<"auto" | "manual">) => {
      state.isLoggedIn = false;
      state.currentUser = null;
      state.isOTPCompleted = false;
      state.isReadyForOTP = false;
      if (action.payload === "auto") {
        state.loginModalShow = true;
        if (state.timesCalledLogout === 0) {
          toast.warning("פג תוקף החיבור, אנא התחבר שנית");
          state.timesCalledLogout += 1;
        }
      }
      state.isLoading = false;
    },
    setLoginModalShow: (state, action: PayloadAction<boolean>) => {
      state.loginModalShow = action.payload;
      if (!action.payload) state.errorMsg = undefined;
    },
    setAddUserModalShow: (state, action: PayloadAction<boolean>) => {
      state.addUserModalShow = action.payload;
      if (!action.payload) state.errorMsg = undefined;
    },
    setPhoneNumberUpdateModalShow: (state, action: PayloadAction<boolean>) => {
      state.updatePhoneNumModalShow = action.payload;
      if (!action.payload) state.errorMsg = undefined;
    },
    setUpdateFlag: (state) => {
      state.updateFlag = !state.updateFlag;
    },
    setErrorMessage: (state, action: PayloadAction<string | undefined>) => {
      state.errorMsg = action.payload;
    },
    OTPCompleted: (state) => {
      state.isOTPCompleted = true;
      state.loginModalShow = false;
      state.timesCalledLogout = 0;
      if (window.location.pathname !== "/") {
        window.location.reload();
      }
    },
    setIsReadyForOTP: (state, action: PayloadAction<boolean>) => {
      state.isReadyForOTP = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // checkAuthAsync
      .addCase(checkAuthAsync.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        state.currentUser = action.payload;
        state.isAuthInitialized = true;
      })
      .addCase(checkAuthAsync.rejected, (state) => {
        state.isLoggedIn = false;
        state.currentUser = null;
        state.isAuthInitialized = true;
      })
      // LoginAsync
      .addCase(LoginAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMsg = "";
      })
      .addCase(LoginAsync.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        state.currentUser = action.payload;
        state.isAuthInitialized = true;
      })
      .addCase(LoginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.currentUser = null;
        state.errorMsg = (action.payload as string) || "שגיאה בהתחברות";
      })
      // Admin lists
      .addCase(getAllUsersAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllUsersAsync.fulfilled, (state, action) => {
        state.usersList = action.payload;
        state.isLoading = false;
      })
      .addCase(getAllUsersAsync.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addUserAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addUserAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addUserAsync.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  localLogout,
  setErrorMessage,
  setLoginModalShow,
  setAddUserModalShow,
  setUpdateFlag,
  setPhoneNumberUpdateModalShow,
  OTPCompleted,
  setIsReadyForOTP
} = authenticationSlice.actions;

export const SelectCurrentUser = (state: RootState) => state.Login.currentUser;
export const selectLoginModalShow = (state: RootState) => state.Login.loginModalShow;
export const selectAddUserModalShow = (state: RootState) => state.Login.addUserModalShow;
export const selectUpdatePhoneNumModalShow = (state: RootState) => state.Login.updatePhoneNumModalShow;
export const SelectIsLoading = (state: RootState) => state.Login.isLoading;
export const SelectErrorMessage = (state: RootState) => state.Login.errorMsg;
export const SelectUsersList = (state: RootState) => state.Login.usersList;
export const SelectUpdateFlag = (state: RootState) => state.Login.updateFlag;
export const SelectIsReadyForOTP = (state: RootState) => state.Login.isReadyForOTP;
export const SelectIsLogged = (state: RootState) => state.Login.isLoggedIn;
export const SelectIsAuthInitialized = (state: RootState) => state.Login.isAuthInitialized;

export default authenticationSlice.reducer;